import React, { useState, useEffect } from 'react'
import i18n, { useTranslation } from '@zauberware/react-i18n/web'
import { Grid } from 'antd'
import { isEqual } from 'lodash'
import { Chart, Graph } from '../../assets/icons'
import { SwitchGroup, ComposedChart } from '../../components/atoms'
import { TitleWithX, DoughnutChartWithStatistics, Legend } from '../../components/molecules'
import { ListSelect, FactListDetail } from '../../components/organisms'

const { useBreakpoint } = Grid

const listSelects = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(
  i => `listSelectName${i}`
)
const years = [
  { text: '2016', value: '2016' },
  { text: '2017', value: '2017' },
  { text: '2018', value: '2018' },
  { text: '2019', value: '2019' },
  { text: '2020', value: '2020' },
]

const Umsatz = () => {
  const [data, setData] = useState()
  const [texts, setTexts] = useState()
  const [detailData, setDetailData] = useState()
  const [detailTexts, setDetailTexts] = useState()
  const { t } = useTranslation(['umsatz', 'bereiche'])
  const screens = useBreakpoint()
  const mobile = !screens.lg
  const chartTypes = [
    { text: mobile ? null : t('numbers'), value: 'doughnut', icon: <Chart /> },
    { text: mobile ? null : t('graph'), value: 'graph', icon: <Graph /> },
  ]
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/jsons/Umsatz/texts.json`)
      .then(res => res.json())
      .then(d => setTexts(d))
      .catch(err => {
        console.error(err, ' error')
      })

    fetch(`${process.env.PUBLIC_URL}/jsons/Bereiche/data.json`)
      .then(res => res.json())
      .then(d => setDetailData(d))
      .catch(err => {
        console.error(err, ' error')
      })

    fetch(`${process.env.PUBLIC_URL}/jsons/Bereiche/text.json`)
      .then(res => res.json())
      .then(d => setDetailTexts(d))
      .catch(err => {
        console.error(err, ' error')
      })

    years.forEach(year => {
      fetch(`${process.env.PUBLIC_URL}/jsons/Umsatz/numbers/numbers_${year.value}.json`)
        .then(res => res.json())
        .then(d =>
          setData(old => ({
            ...old,
            numbers: { ...old?.numbers, ...d },
          }))
        )
        .catch(err => {
          console.error(err, ' error')
        })
    })
  }, [])

  const [activeFilter, setActiveFilter] = useState('alle')
  const [detailFilter, setDetailFilter] = useState(null)
  const [activeElement, setActiveElement] = useState(null)
  const [activeYear, setActiveYear] = useState(years[4])
  const [chartType, setChartType] = useState(chartTypes[0])
  const [doughnutData, setDoughnutData] = useState()
  const [listSelectData, setListSelectData] = useState()
  const [dotPos, setDotPos] = useState(4)

  const [dataAreaChart, setDataAreaChart] = useState()
  const [dataLineChart, setDataLineChart] = useState()

  useEffect(() => {
    if (activeYear && data?.numbers[activeYear?.value] && texts) {
      setDoughnutData({
        doughnutData: {
          ...data?.numbers[activeYear?.value]?.upperArea?.doughnutData,
          activeElement:
            activeFilter !== 'alle' &&
            data?.numbers[activeYear?.value].upperArea.doughnutData.datas.filter(
              item => item.title === activeFilter
            )[0],
          description:
            activeFilter === 'alle'
              ? t(texts?.doughnutData.description)
              : t(texts?.doughnutData.descriptionFiltered),
          all: activeFilter === 'alle',
        },
        statistics: {
          column1: [
            ...data?.numbers[activeYear?.value].upperArea.statistics.alle.column1.map((col, i) => ({
              ...col,
              percentage:
                (col.value /
                  data?.numbers[activeYear?.value].upperArea.statistics.alle.column1[0].value) *
                100,
              description: t(texts?.statistics.column1[i]),
            })),
          ],
          column2: [
            ...data?.numbers[activeYear?.value].upperArea.group.map((col, i) => ({
              ...col,
              description: t(texts?.statistics.group[i]),
            })),
          ],
          buttonLabel: t(texts?.statistics.button),
          title: t('titleAll'),
          description: t('listSelectDescription', {
            year: activeYear?.value,
          }),
        },
      })
    }

    if (activeYear && activeYear.value && data && data.numbers && data.numbers[activeYear?.value]) {
      setListSelectData(
        listSelects.map(item => {
          const { datas } = data?.numbers[activeYear?.value].upperArea.doughnutData
          const curItem = datas?.filter(i => i.title === item)
          return { key: curItem[0]?.title, value: curItem[0]?.value }
        })
      )
    }
    if (detailFilter?.activeYear !== activeYear?.value) {
      setDetailFilter(selectState => ({ ...selectState, activeYear: activeYear?.value }))
    }
    setDotPos(years.findIndex(year => isEqual(year, activeYear)))
  }, [activeFilter, activeYear, data, detailFilter, t, texts])

  useEffect(() => {
    if (detailFilter && detailFilter.value) {
      const selection = detailData[detailFilter.ns][detailFilter.activeYear]
      const element = selection.find(ele => ele.key === detailFilter.value)

      if (element) {
        setActiveElement(element)
      } else {
        setActiveElement(null)
      }
    } else {
      setActiveElement(null)
    }
  }, [detailData, detailFilter])

  useEffect(() => {
    setDetailFilter(selectState => ({ ...selectState, key: activeElement?.value }))
  }, [activeElement])

  useEffect(() => {
    let areaChart = []
    let lineChart = []
    if (activeYear && activeYear.value && data && data.numbers && data.numbers[activeYear?.value]) {
      areaChart = years.map(year => ({
        name: year.value,
        area: data.numbers[year.value]?.upperArea.doughnutData.datas.find(
          item => item.title === activeFilter
        )?.value,
      }))
      lineChart = years.map(year => ({
        name: year.value,
        [activeFilter === 'alle' ? 'area' : 'line']: data?.numbers[year.value]?.upperArea.statistics
          .alle.column1[0].value,
      }))
    }

    if (activeFilter === 'alle') {
      setDataAreaChart(null)
      setDataLineChart(lineChart)
    } else {
      setDataAreaChart(areaChart)
      setDataLineChart(lineChart)
    }
  }, [activeFilter, activeYear, data])

  const { language } = i18n
  const downloadableObj =
    language === 'de'
      ? {
          name: 'Die Würth-Gruppe im Überblick',
          ref:
            '../../../../media/downloads/pdf/download_center_pdfs/Ueberblick_Wuerth-Gruppe_2020.pdf',
          size: 281019,
        }
      : {
          name: 'Würth Group at a glance 2020',
          ref:
            '../../../../media/downloads/pdf/download_center_pdfs/en_pdfs/At_a_glance_-_Wuerth_Group_2020.pdf',
          size: 280122,
        }

  return (
    <div>
      <div
        style={{
          paddingBottom: mobile ? '25px' : '40px',
          ...(mobile
            ? { display: 'flex', justifyContent: 'space-between' }
            : {
                display: 'flex',
                margin: '0 auto',
                justifyContent: 'center',
                position: 'relative',
                alignItems: 'center',
              }),
        }}
      >
        <div
          style={
            mobile ? {} : { position: 'absolute', top: 0, margin: '0 auto ', paddingTop: '4px' }
          }
        >
          <TitleWithX
            type="download"
            description={t('common:download')}
            descriptionActive={t('common:downloaded')}
            title={t('keyfigures')}
            downloadableObj={downloadableObj}
          />
        </div>
        <div style={mobile ? {} : { position: 'absolute', right: 0, top: 0 }}>
          <SwitchGroup
            data={chartTypes}
            value={chartType}
            onChange={setChartType}
            tinyLabel={mobile}
          />
        </div>
      </div>

      {!detailFilter?.value && (
        <>
          {chartType.value === 'doughnut' && doughnutData && (
            <>
              <div
                style={{
                  width: '100%',
                  margin: '0 auto',
                  display: 'flex',
                  justifyContent: 'center',
                  padding: mobile ? '' : '32px 0',
                }}
              >
                <SwitchGroup
                  data={years}
                  value={activeYear}
                  onChange={setActiveYear}
                  tinyLabel={mobile}
                  flex={mobile}
                />
              </div>
              <DoughnutChartWithStatistics
                data={doughnutData}
                namespace="umsatz"
                detailButton={!(activeFilter === 'alle')}
                isFilterActive={activeFilter === 'alle'}
                onClick={() =>
                  setDetailFilter({
                    activeYear: activeYear.value,
                    value: activeFilter,
                    ns: [1, 4, 7, 10, 13].find(
                      i => i === parseInt(activeFilter.match(/\d/g).join(''))
                    )
                      ? 'wuerth'
                      : 'allied',
                  })
                }
              />
            </>
          )}
          {chartType.value === 'graph' && (
            <>
              <div style={{ marginTop: 32, marginBottom: 16 }}>
                <Legend
                  title={t('titleAll')}
                  value={
                    data?.numbers[activeYear?.value]?.upperArea?.statistics?.alle?.column1[0]?.value
                  }
                  subtitle={t('millionEuro')}
                  left={10}
                  top={-23}
                  isActive={activeFilter === 'alle'}
                />
                <ComposedChart
                  dataAreaChart={dataAreaChart}
                  dataLineChart={dataLineChart}
                  dotPos={dotPos}
                />
              </div>
              <div style={{ width: '100%', margin: '0 auto', padding: mobile ? '' : '32px 0' }}>
                <SwitchGroup
                  data={years}
                  value={activeYear}
                  onChange={setActiveYear}
                  tinyLabel={mobile}
                  flex
                />
              </div>
            </>
          )}

          <div style={{ paddingTop: 32 }}>
            <ListSelect
              namespace="umsatz"
              title={t('businessUnitsTitle')}
              description={t('businessUnitsDescription')}
              onChange={e => {
                const selected = listSelectData?.find(item => item.key === Object.keys(e)[0])
                return setActiveFilter(selected?.key || 'alle')
              }}
              elements={listSelectData?.map((element, i) => ({
                ...element,
                name: texts.listSelect[i],
              }))}
              onReset={() => setActiveFilter('alle')}
              active={activeFilter}
            />
          </div>
        </>
      )}
      {detailFilter && activeElement?.value && (
        <div
          style={{
            width: '100%',
            paddingTop: mobile && '24px',
          }}
        >
          <FactListDetail
            element={{
              ...activeElement,
              statistics: activeElement.statistics.map((elem, i) => ({
                ...elem,
                title: t(`bereiche:${detailTexts?.statistics?.[i]}`),
              })),
              title: t(
                `bereiche:${detailTexts?.[detailFilter?.ns]?.['2020']?.[detailFilter?.key]?.title}`
              ),
              subtitle: t(
                `bereiche:${
                  detailTexts?.[detailFilter?.ns]?.['2020']?.[detailFilter?.key]?.subTitle
                }`
              ),
              content: detailTexts?.[detailFilter?.ns]?.['2020'][detailFilter?.key]?.content.map(
                ele => ({
                  title: t(`bereiche:${ele.title}`),
                  paragraph: t(`bereiche:${ele.paragraph}`),
                })
              ),
            }}
            onClear={() => setDetailFilter(null)}
          />
        </div>
      )}
    </div>
  )
}

export default Umsatz
